import { useCallback, useContext, useMemo } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { Loader } from '../Common/Loader';
import { useTranslate } from '@/hooks/useTranslate';
import { useSelectedPaymentMethod } from '@/hooks/useSelectedPaymentMethod';
import { usePayCtaIdlePulse } from '@/hooks/usePayCtaIdlePulse';
import { cn } from '@/utils/cn';
import { trackPayButtonBlockedClick } from '@/utils/conversionTracking';
import { formatTotalPrice } from '@/utils/pricing';

interface PayButtonProps {
  onClick?: () => void;
  isDisabled?: boolean;
}

export const PayButton = ({ onClick, isDisabled }: PayButtonProps) => {
  const context = useContext(CheckoutContext);
  const {
    payment: { isInPaymentProcess, isFormValid },
    actions: { runPayment },
    subscription,
    pricing,
  } = context;
  const price = formatTotalPrice(pricing);

  const { t } = useTranslate();

  const isButtonDisabled = useMemo(() => {
    return isDisabled || isInPaymentProcess;
  }, [isDisabled, isInPaymentProcess]);

  const { selectedPaymentMethod } = useSelectedPaymentMethod();

  const handleClick = useCallback(() => {
    if (!isButtonDisabled) {
      if (onClick) {
        onClick();
      } else {
        runPayment({ paymentMethodName: selectedPaymentMethod });
      }
      return;
    }
    // Disabled tap that is NOT due to an in-flight payment ⇒ the form is
    // invalid/incomplete. Records pay intent the validation gate swallowed.
    if (!isInPaymentProcess) {
      trackPayButtonBlockedClick(context);
    }
  }, [context, isButtonDisabled, isInPaymentProcess, onClick, selectedPaymentMethod, runPayment]);

  const { isPulsing, interactionHandlers } = usePayCtaIdlePulse({
    isActionable: !isButtonDisabled && isFormValid,
  });

  return (
    <div
      data-testid="pay-button"
      data-pulse-active={isPulsing ? 'true' : undefined}
      className={cn(
        'w-full bg-blue h-12 rounded-md flex items-center justify-center p-spacing-2 gap-2 cursor-pointer',
        isButtonDisabled && 'opacity-50 cursor-not-allowed',
        isPulsing && 'pay-cta-idle-pulse--active'
      )}
      onClick={handleClick}
      {...interactionHandlers}
    >
      {isInPaymentProcess ? (
        <Loader size="xs" color="white" />
      ) : (
        <div data-testid="pay-button-text" className={`text-white font-medium text-lg md:cursor-pointer`}>
          {/* ROSCA: recurring purchases must be labelled as such on the CTA. */}
          {(subscription ? t('subscribeAndPay') : t('pay')) + ' ' + price}
        </div>
      )}
    </div>
  );
};
